import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Tabs, List, Tag, Typography, message, Space, Select, } from 'antd';
import { CustomerServiceOutlined, SendOutlined, HistoryOutlined, MessageOutlined } from '@ant-design/icons';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

const HelpDesk = () => {
    const { user } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    const isAdmin = user?.role === 'admin';

    const fetchTickets = async () => {
        setLoading(true);
        try {
            const res = await api.get('/help-desk');
            setTickets(res.data);
        } catch (e) {
            message.error('Failed to load tickets');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchTickets(); }, []);

    const onFinish = async (values) => {
        setSubmitting(true);
        try {
            await api.post('/help-desk', values);
            message.success('Ticket submitted successfully!');
            form.resetFields();
            fetchTickets(); // Refresh the list
        } catch (e) {
            message.error('Failed to submit ticket.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleStatusChange = async (ticketId, newStatus) => {
        try {
            await api.patch(`/admin/help-desk/${ticketId}/status`, { status: newStatus });
            message.success(`Ticket marked as ${newStatus.replace('_', ' ')}`);
            fetchTickets();
        } catch (e) {
            message.error('Failed to update status');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'closed': return 'success';
            case 'in_progress': return 'processing';
            default: return 'warning';
        }
    };

    const items = [
        // Only show "Submit" tab for non-admins
        ...(!isAdmin ? [{
            key: '1',
            label: <span><MessageOutlined /> Submit Ticket</span>,
            children: (
                <Form form={form} layout="vertical" onFinish={onFinish} style={{ marginTop: 20 }}>
                    <Form.Item name="subject" label="Subject" rules={[{ required: true }]}>
                        <Input placeholder="What is the issue?" />
                    </Form.Item>
                    <Form.Item name="description" label="Detailed Description" rules={[{ required: true }]}>
                        <Input.TextArea rows={4} placeholder="Tell us more so we can help..." />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" icon={<SendOutlined />} block loading={submitting} style={{ backgroundColor: '#FE1F08' }}>
                        Submit
                    </Button>
                </Form>
            )
        }] : []),
        {
            key: '2',
            label: <span><HistoryOutlined /> {isAdmin ? 'Manage All Tickets' : 'My Tickets'}</span>,
            children: (
                <List
                    loading={loading}
                    dataSource={tickets}
                    style={{ marginTop: 20 }}
                    renderItem={(item) => (
                        <List.Item>
                            <Card style={{ width: '100%' }} size="small" hoverable>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Space direction="vertical" size={0}>
                                        <Text strong>{item.subject}</Text>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            {isAdmin ? `From: ${item.user?.name} (${item.user?.role})` : `Submitted: ${new Date(item.created_at).toLocaleDateString()}`}
                                        </Text>
                                    </Space>
                                    
                                    {isAdmin ? (
                                        <Select 
                                            defaultValue={item.status} 
                                            style={{ width: 130 }} 
                                            onChange={(val) => handleStatusChange(item.id, val)}
                                        >
                                            <Option value="open">Open</Option>
                                            <Option value="in_progress">In Progress</Option>
                                            <Option value="closed">Closed</Option>
                                        </Select>
                                    ) : (
                                        <Tag color={getStatusColor(item.status)}>
                                            {item.status.replace('_', ' ').toUpperCase()}
                                        </Tag>
                                    )}
                                </div>
                                <Paragraph style={{ marginTop: 10, color: '#555' }}>{item.description}</Paragraph>
                            </Card>
                        </List.Item>
                    )}
                />
            )
        }
    ];

    return (
        <div style={{ maxWidth: 850, margin: '20px auto' }}>
            <Card>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <CustomerServiceOutlined style={{ fontSize: 40, color: '#FE1F08' }} />
                    <Title level={3}>{isAdmin ? 'Admin Help Desk Management' : 'Help Desk'}</Title>
                    {isAdmin && <Text type="secondary">Review and resolve technical issues from Mentees and Mentors.</Text>}
                </div>
                <Tabs defaultActiveKey={isAdmin ? "2" : "1"} items={items} centered />
            </Card>
        </div>
    );
};

export default HelpDesk;